import { ArEmailGroupByOption, EmailQueueStatus, EmailSortByOption, PaginatedResponse, SortOrder } from "@repo/types";
import { ArEmailGroupBySelectOption, ArEmailQueueItem, ArEmailQueueMatter, EMAIL_SORT_BY, EmailQueueResponse, IPopulateMailSendMessage } from "../../../types/emailQueue";
import { showToast } from "@repo/ui";
import { MatterInvoice } from "../../../types";

export function paginateLocal<T>(data: T[], page: number, limit: number): PaginatedResponse<T> {
  const total = data.length;
  const totalPages = Math.ceil(total / limit);
  const start = (page - 1) * limit;
  const end = start + limit;

  return {
    data: data.slice(start, end),
    pagination: {
      page,
      limit,
      total,
      totalPages,
    },
  };
}

export function sortLocal<T>(data: T[], sorts: Partial<Record<EMAIL_SORT_BY, 'asc' | 'desc'>>) {
  const sorted: T[] = [...data];
  const values = (Object.entries(sorts) as [EMAIL_SORT_BY, 'asc' | 'desc' | undefined][])
    .find(([, order]) => order !== undefined);

  if (values) {
    const [sortBy, order] = values;
    return sorted.sort((a: any, b: any) => {
      let comparison = 0;

      switch (sortBy) {
        case EmailSortByOption.AMOUNT_OUTSTANDING:
          comparison = a.amountOutstanding - b.amountOutstanding;
          break;

        case EmailSortByOption.MAX_DAYS_PAST_DUE:
          comparison = a.maxDaysPastDue - b.maxDaysPastDue;
          break;

        default:
          comparison = 0;
      }

      return order === SortOrder.ASC ? comparison : -comparison;
    });
  }

  return sorted;
}

export function searchTableLocal<T extends { matterDisplayNumber?: string; invoiceNumbers?: string[] }>(
  data: T[],
  keyword: string
): T[] {
  if (!keyword.trim()) return data;
  const lower = keyword.toLowerCase();
  return data.filter(item =>
    item.matterDisplayNumber?.toLowerCase().includes(lower) ||
    item.invoiceNumbers?.some(inv => inv.toLowerCase().includes(lower))
  );
}

export function searchTableLocalByClientName<T extends { clientName?: string }>(data: T[], keyword: string): T[] {
  if (!keyword.trim()) return data;

  const lower = keyword.toLowerCase();

  return data.filter(item => item.clientName?.toLowerCase().includes(lower));
}


/**
 * Rows in multi table need key unique to handle
 *  */
export function getRowUniqueKey(row: any) {
  if (!row) return '';

  if ('matterId' in row && row.matterId) {
    return `mat-${row.clientId}-${row.matterId}`;
  }

  if ('invoiceNumbers' in row && row.invoiceNumbers) {
    return `inv-${row.clientId}-${row.invoiceNumbers.join('_')}`;
  }

  if ('clientId' in row) {
    return `client-${row.clientId}`;
  }

  return 'row-' + JSON.stringify(row);
}

export function transformDataAttorney(data: ArEmailQueueItem[]): ArEmailQueueItem[] {
  if (!data) return [];
  const clientMap = new Map<string, ArEmailQueueMatter[]>();
  data.forEach(matter => {
    if (!clientMap.has(matter.clientId)) {
      clientMap.set(matter.clientId, []);
    }
    clientMap.get(matter.clientId)!.push(matter as any);
  });

  return Array.from(clientMap.values())
    .map(mattersOfClient => {
      const firstMatter = mattersOfClient[0]!;
      const populateItem: ArEmailQueueItem = {
        ...firstMatter,
        amountOutstanding: mattersOfClient.reduce((acc, curr) => acc + curr.amountOutstanding, 0),
        maxDaysPastDue: mattersOfClient.reduce((acc, curr) => Math.max(acc, curr.maxDaysPastDue), 0),
        matters: mattersOfClient,
        numOfItems: mattersOfClient.length
      } as any;
      return populateItem;
    })
    .filter((item) => item !== null)
}

export const emailGroupByOptions: ArEmailGroupBySelectOption[] = [
  { label: 'No Grouping', value: ArEmailGroupByOption.NONE },
  { label: 'Group by Client', value: ArEmailGroupByOption.CLIENT },
  { label: 'Group by Originating Attorney', value: ArEmailGroupByOption.ORIGINATING },
  { label: 'Group by Responsible Attorney', value: ArEmailGroupByOption.RESPONSIBLE },
];

export const emailGroupByAttorneyOriginalOptions: ArEmailGroupBySelectOption[] = [
  { label: 'Group by Client', value: ArEmailGroupByOption.CLIENT },
  { label: 'Group by Originating Attorney', value: ArEmailGroupByOption.ORIGINATING },
];

export const emailGroupByAttorneyResponseOptions: ArEmailGroupBySelectOption[] = [
  { label: 'Group by Client', value: ArEmailGroupByOption.CLIENT },
  { label: 'Group by Responsible Attorney', value: ArEmailGroupByOption.RESPONSIBLE },
];

const updateQueryData = (oldData: EmailQueueResponse | undefined, matter: ArEmailQueueMatter | undefined, groupBy: ArEmailGroupByOption): EmailQueueResponse | undefined => {
  if (!oldData?.data) return oldData;
  if (!matter) return oldData;

  const { clientId, originatingAttorneyId, responsibleAttorneyId } = matter;
  const updateData = oldData.data
    .map(group => {
      const matchGroup =
        (groupBy === ArEmailGroupByOption.ORIGINATING &&
          group.originatingAttorneyId === originatingAttorneyId) ||
        (groupBy === ArEmailGroupByOption.RESPONSIBLE &&
          group.responsibleAttorneyId === responsibleAttorneyId) ||
        (groupBy === ArEmailGroupByOption.CLIENT && group.clientId === clientId);

      if (!matchGroup) return group;

      return { ...group };
    });
  return {
    ...oldData,
    data: updateData,
  };
};

export const createEmailQueueUpdater =
  (matter: ArEmailQueueMatter | undefined, groupBy: ArEmailGroupByOption) =>
    (oldData: EmailQueueResponse | undefined) => {
      return updateQueryData(oldData, matter, groupBy);
    };

export const populateEmailMessage = <T>({ error = undefined, email }: IPopulateMailSendMessage<T>): void => {
  if (error) {
    showToast.error(`Email(s) ${email} has been sent failed.`);
  }
  else {
    showToast.success(`Email ${email} has been sent successfully.`);
  }
};

export function populateMatterInvoiceSummary(
  invoices: MatterInvoice[],
  currentClientId?: string | null,
  currentMatterId?: string | null
) {
  const filterInvoices =
    invoices.filter(
      iv => !iv.isCarrierInPreview || iv.emailQueueStatus !== EmailQueueStatus.CLOSED
    ) ?? [];
  if (filterInvoices.length === 0) return null;

  return {
    matterId: currentMatterId,
    matterDisplayNumber: filterInvoices[0]?.matterDisplayNumber,
    matterDescription: filterInvoices[0]?.matterDescription,
    originatingAttorneyName: filterInvoices[0]?.originatingAttorneyName ?? 'Unassigned',
    responsibleAttorneyName: filterInvoices[0]?.responsibleAttorneyName ?? 'Unassigned',
    invoiceNumbers: filterInvoices.map(inv => inv.invoiceNumber),
    amountOutstanding: filterInvoices.reduce((sum, inv) => sum + (inv.arBalance || 0), 0),
    clientId: currentClientId,
    clientName: filterInvoices[0]?.clientName,
    clientEmail: filterInvoices[0]?.clientEmail,
    clientType: filterInvoices[0]?.clientType,
    maxDaysPastDue:
      filterInvoices.length > 0 ? Math.max(...invoices.map(inv => inv.daysPastDue || 0)) : 0,
  };
}